// HUD: Ressourcen, Energie, Erfahrung, Zone, Booster, Meldungen.
//
// Bewusst **DOM** und nicht Phaser: Text und Listen sind im Browser billiger,
// zugaenglicher und ohne Schriftatlas lesbar. Die Zweiteilung — Welt auf
// Canvas, Oberflaeche im DOM — ist dieselbe wie im Vorlaeufer und aus denselben
// Gruenden (VIEW-001).
//
// Zwei Regeln halten das HUD schnell:
//
// * **DOM-Referenzen werden einmal aufgeloest**, nicht je Frame gesucht.
// * **Listen bauen nur bei echter Aenderung neu** — sonst zerstoert der
//   Neubau Hover, Fokus und Scrollposition, sichtbar als „die Liste flackert".

import { bus } from '../core/bus';
import { coordStr, distStr, escapeHtml, fmt, timeStr } from '../core/format';
import { clamp } from '../core/math';
import {
  balancing, boosters, fleetOrder, nodes, resource, resources, zoneAtDist, zonesFor,
} from '../data/balancing';
import { resIconSvg } from '../gfx/icons';
import { countStates, treeStatus } from '../sim/tree';
import { store } from '../state/store';
import { oreTipHtml } from './help';
import { hideTooltip, showTooltip } from './tooltip';

const el = (id: string): HTMLElement => {
  const found = document.getElementById(id);
  if (!found) throw new Error(`HUD-Element fehlt: ${id}`);
  return found;
};

export interface HudSource {
  distToBase(): number;
  /**
   * Wo das Schiff steht — die Welt ist endlos, und ohne Koordinate lässt sich
   * kein Ort merken und keiner nennen.
   *
   * Zwei Zahlen statt eines Punktes: ein Objekt je Bild wäre Müll im Takt
   * (Gate FG-14), und mehr als zwei Zahlen braucht die Anzeige nicht.
   */
  shipX(): number;
  shipY(): number;
  docked(): boolean;
  claimable(): number;
  buildable(): boolean;
  /** Name der Anomalie, in der das Schiff steht — `null` ausserhalb. */
  anomaly(): string | null;
  /** Der laufende Kurs des Autopiloten — `null`, wenn keiner läuft. */
  course(): { x: number; y: number; label: string } | null;
  /** Zustand des Sprungfelds. `null`, solange es die Szene nicht gibt. */
  warp(): { wish: boolean; on: boolean; progress: number } | null;
  /**
   * Die Umlaufbahn (Akt III): der Planet unter dem Schiff und ob dort schon
   * eine Kapsel steht — `null` außerhalb.
   */
  orbit(): { name: string; founded: boolean } | null;
}

export class Hud {
  private readonly nodes = {
    resbar: el('resbar'),
    energyFill: el('energy-fill'),
    energyPct: el('energy-pct'),
    hullFill: el('hull-fill'),
    hullPct: el('hull-pct'),
    pointMark: el('point-mark'),
    pointCount: el('point-count'),
    xpFill: el('xp-fill'),
    xpText: el('xp-text'),
    shipLevel: el('ship-level'),
    zoneName: el('zone-name'),
    zonePos: el('zone-pos'),
    zoneSub: el('zone-sub'),
    badgeUpg: el('badge-upg'),
    badgeBase: el('badge-base'),
    badgeQuest: el('badge-quest'),
    badgePoints: el('badge-points'),
    boosterStrip: el('boosterstrip'),
    toasts: el('toasts'),
    netStatus: el('net-status'),
  };

  private boosterKey = '';
  private resKey = '';
  /** Welches Erz der Tooltip gerade zeigt — leer heisst „keiner steht". */
  private resTip = '';
  /**
   * Restdauer der laufenden Notabschaltung in Sekunden; 0 heisst „vorbei".
   *
   * Sie kommt ueber den Bus, weil `scenes` und `ui` sich nicht kennen
   * (Gate FG-04). Gezaehlt wird dort, angezeigt hier.
   */
  private towLeft = 0;
  /**
   * Wer gerade auf das Schiff feuert. Leerer Name heisst „kein Gefecht".
   *
   * Seit der Abbau im Gefecht pausiert, ist das keine Verzierung: der
   * Wachbereich einer Schwarmmutter misst 780 Einheiten, und ohne diesen Chip
   * saehe der Spieler seinen Abbau aussetzen und faende keinen Grund.
   */
  private combat = { count: 0, name: '' };

  constructor(private readonly source: HudSource) {
    this.buildResBar();
    bus.on('toast', ({ text, tone }) => this.toast(text, tone ?? ''));
    bus.on('tow', ({ left }) => { this.towLeft = left; });
    bus.on('combat', (state) => { this.combat = state; });
    bus.on('net:status', (status) => this.setNetStatus(status));
    bus.on('res:gained', ({ res }) => this.pulseResource(res));
    this.nodes.pointMark.onclick = () => bus.emit('panel:toggle', { tab: 'points' });
    this.wireResTips();
  }

  /**
   * „Wo finde ich dieses Erz?" — der Tooltip an der Erzleiste.
   *
   * Die Chips tragen ihre ID schon als `data-res`; die Verdrahtung laeuft
   * darum ueber **Delegation** an der Leiste und nicht ueber 12 Zuhoerer, die
   * `buildResBar` bei jedem Neubau erneut anlegen muesste.
   *
   * **Ein Finger kennt kein Zeigen.** Auf Touch gibt es keinen Zustand vor dem
   * Druecken, also wird der Treffer beim `pointerdown` ausgewertet und der
   * Tooltip umgeschaltet — derselbe Chip nochmal blendet ihn aus. Ein Chip hat
   * sonst keine Bedeutung, der Tipp kollidiert also mit nichts.
   *
   * Er haengt am **Chip**, nicht am Zeiger — wie der Tooltip des Baums an
   * seinem Knoten. Das spart nicht nur den Neubau bei jeder Mausbewegung: am
   * Zeiger klebend verdeckt ihn auf Touch der eigene Finger.
   */
  private wireResTips(): void {
    const chipAt = (event: PointerEvent): HTMLElement | null =>
      (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-res]') ?? null;

    this.nodes.resbar.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      const chip = chipAt(event);
      if (!chip) { this.hideResTip(); return; }
      if (chip.dataset.res !== this.resTip) this.showResTip(chip);
    });
    this.nodes.resbar.addEventListener('pointerleave', () => this.hideResTip());

    this.nodes.resbar.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      const chip = chipAt(event);
      if (!chip || chip.dataset.res === this.resTip) { this.hideResTip(); return; }
      this.showResTip(chip);
    });

    // Er haengt an keinem Panel — ohne diese Abmeldung stuende er ueber der
    // Abdunklung, waehrend der Spieler laengst im Baum liest.
    bus.on('panel:state', () => this.hideResTip());
  }

  private showResTip(chip: HTMLElement): void {
    const res = resource(chip.dataset.res ?? '');
    if (!res) return;
    this.resTip = res.id;
    const box = chip.getBoundingClientRect();
    showTooltip(oreTipHtml(res, store.save.era), box.left, box.bottom);
  }

  private hideResTip(): void {
    if (!this.resTip) return;
    this.resTip = '';
    hideTooltip();
  }

  private buildResBar(): void {
    this.nodes.resbar.innerHTML = resources()
      .map((res) => `<div class="res" data-res="${res.id}" style="display:none">
        ${resIconSvg(res)}<span class="amt">0</span></div>`)
      .join('');
  }

  /** Einmal je Frame. Liest den Zustand und schreibt ihn nie. */
  update(): void {
    this.updateResources();
    this.updateBars();
    this.updateZone();
    this.updateBadges();
    this.updateBoosters();
  }

  private updateResources(): void {
    const key = resources().map((res) => Math.floor(store.save.res[res.id] ?? 0)).join('|');
    if (key === this.resKey) return;
    this.resKey = key;

    for (const res of resources()) {
      const row = this.nodes.resbar.querySelector<HTMLElement>(`[data-res="${res.id}"]`);
      if (!row) continue;
      const seen = (store.save.totalRes[res.id] ?? 0) > 0 || (store.save.res[res.id] ?? 0) > 0;
      row.style.display = seen ? '' : 'none';
      if (!seen) continue;
      const amount = row.querySelector('.amt');
      const text = fmt(store.save.res[res.id] ?? 0);
      if (amount && amount.textContent !== text) amount.textContent = text;
    }
  }

  /**
   * Der Huellenbalken.
   *
   * Er steht **neben** der Energie, nicht darueber: beides ist Reibung. Bei
   * null wird geschleppt, nicht gestorben — der Text sagt das auch, damit
   * niemand einen Verlust vermutet, den es nicht gibt.
   */
  /**
   * Die offenen Level-Punkte — die gruenen Pfeile.
   *
   * Sie stehen am Levelbalken, weil sie von dort kommen. Ohne sichtbare Marke
   * sammeln sich Punkte an, die niemand ausgibt.
   *
   * Und die Marke **fuehrt** seit dem eigenen Reiter auch dorthin: sie zu
   * sehen und dann suchen zu muessen, wo man sie ausgibt, war der halbe Weg.
   */
  private updatePoints(): void {
    const offen = store.save.ship.points;
    this.nodes.pointMark.classList.toggle('hidden', offen <= 0);
    if (offen > 0) this.nodes.pointCount.textContent = String(offen);
  }

  private updateHull(): void {
    const max = Number(store.stats.values.hullMax);
    const towing = this.towLeft > 0;
    this.nodes.hullFill.classList.toggle('towing', towing);
    if (towing) {
      // Der Rücklauf: der Balken leert sich über die Dauer aus der Tabelle,
      // damit die verlorene Zeit ein Ende hat, das man sieht.
      const rest = clamp(this.towLeft / balancing().towSeconds, 0, 1);
      this.nodes.hullFill.style.width = (rest * 100).toFixed(1) + '%';
      this.nodes.hullPct.textContent = `Notabschaltung — noch ${timeStr(this.towLeft)}`;
      return;
    }
    const share = clamp(store.save.ship.hp / max, 0, 1);
    this.nodes.hullFill.style.width = (share * 100).toFixed(1) + '%';
    this.nodes.hullPct.textContent = share < 0.25
      ? `${Math.round(share * 100)}% — Notabschaltung droht`
      : `${Math.round(share * 100)}%`;
  }

  /** Den Chip einer Ressource kurz aufleuchten lassen. */
  private pulseResource(res: string): void {
    const row = this.nodes.resbar.querySelector<HTMLElement>(`[data-res="${res}"]`);
    if (!row) return;
    // Neu anstossen, auch wenn die Animation noch laeuft: ohne den Neustart
    // pulst nur der erste Posten einer Salve.
    row.classList.remove('gain');
    void row.offsetWidth;
    row.classList.add('gain');
  }

  private updateBars(): void {
    this.updateHull();
    this.updatePoints();
    const max = Number(store.stats.values.energyMax);
    const share = clamp(store.save.ship.energy / max, 0, 1);
    this.nodes.energyFill.style.width = (share * 100).toFixed(1) + '%';
    this.nodes.energyPct.textContent = this.source.docked()
      ? `${Math.round(share * 100)}% ▲ lädt`
      : share <= 0.001
        ? '0% — zurück zur Kapsel'
        : `${Math.round(share * 100)}%`;

    const need = store.xpNeeded;
    this.nodes.xpFill.style.width = clamp(store.save.ship.xp / need, 0, 1) * 100 + '%';
    this.nodes.xpText.textContent = `${fmt(store.save.ship.xp)} / ${fmt(need)}`;
    this.nodes.shipLevel.textContent = String(store.save.ship.level);
  }

  private updateZone(): void {
    const dist = this.source.distToBase();
    const zone = zonesFor(store.save.era)[zoneAtDist(dist, store.save.era)];
    this.nodes.zoneName.textContent = zone.name;
    this.nodes.zoneSub.textContent = `${zone.sub} · ${distStr(dist)}`;
    // **An ihren Platz geschrieben, nicht über einen Neubau.** Sie ändert sich
    // in jedem Bild; ein Rerender dafür zöge dem Spieler die Knöpfe darunter
    // weg (`oberflaeche.md`).
    this.nodes.zonePos.textContent = coordStr(this.source.shipX(), this.source.shipY());
  }

  /**
   * Die Zahl am Reiter kommt aus **derselben** Auswertung wie die Farbe der
   * Kachel und die Zahl in der Legende (`sim/tree.ts`).
   *
   * Vorher zaehlte das HUD selbst nach. Das ergab zweimal dieselbe Regel an
   * zwei Stellen — und die Abweichung waere ein Reiter, der „3" verspricht,
   * waehrend der Baum zwei kaufbare Knoten zeigt.
   */
  private updateBadges(): void {
    const buyable = countStates(nodes(), treeStatus(store.save, store.statCard('enemiesByKind'))).buyable;
    this.nodes.badgeUpg.textContent = buyable > 0 ? String(buyable) : '';
    const claimable = this.source.claimable();
    this.nodes.badgeQuest.textContent = claimable > 0 ? String(claimable) : '';
    this.nodes.badgeBase.textContent = this.source.buildable() ? '!' : '';
    // Offene Punkte sind der einzige Fortschritt, der liegen bleibt, ohne dass
    // irgendwo etwas fehlt — sie kosten nichts und laufen nicht ab. Ohne Zahl
    // am Reiter sammeln sie sich an, und niemand gibt sie aus.
    const offen = store.save.ship.points;
    this.nodes.badgePoints.textContent = offen > 0 ? String(offen) : '';
  }

  private updateBoosters(): void {
    const active = boosters().filter((entry) => store.boosterLeft(entry.id) > 0);
    const anomaly = this.source.anomaly();
    const fleet = store.fleet && store.fleet.buffLeft > 0 ? store.fleet : null;
    const fleetName = fleet ? fleetOrder(fleet.order)?.name ?? fleet.order : '';
    // Anomalie und Feld-Buff gehoeren in den Aenderungsschluessel: ohne sie
    // bliebe der Chip stehen, bis der naechste Booster den Streifen neu baut.
    // Kurs und Sprungfeld gehören in den Änderungsschlüssel: ohne sie bliebe
    // der Chip stehen, bis zufällig ein Booster den Streifen neu baut.
    const course = this.source.course();
    const warp = this.source.warp();
    const kurs = course ? `${course.label}|${Math.round(Math.hypot(course.x, course.y) / 50)}` : '';
    const sprung = warp ? `${warp.wish}${warp.on}${Math.round(warp.progress * 10)}` : '';
    // Die Umlaufbahn gehört in den Schlüssel wie der Kurs: sie wechselt selten,
    // und ohne sie bliebe der Chip stehen, nachdem man weitergeflogen ist.
    const orbit = this.source.orbit();
    const bahn = orbit ? `${orbit.name}|${orbit.founded}` : '';
    const key = active.map((entry) => entry.id + Math.ceil(store.boosterLeft(entry.id))).join('|')
      + '§' + (anomaly ?? '')
      + '§' + (fleet ? fleetName + Math.ceil(fleet.buffLeft) : '')
      + '§' + kurs + '§' + sprung + '§' + bahn
      + '§' + this.combat.count + this.combat.name;
    if (key === this.boosterKey) return;
    this.boosterKey = key;
    this.nodes.boosterStrip.innerHTML =
      (warp?.wish
        ? `<div class="boostchip warp">${warp.on
          ? 'Lichtflug — Waffen aus'
          : `Sprungfeld lädt … ${Math.round(warp.progress * 100)}%`}</div>`
        : '')
      + (course
        ? `<div class="boostchip course">Autopilot → ${escapeHtml(course.label)}</div>`
        : '')
      + (orbit
        ? `<div class="boostchip orbit">Umlaufbahn: ${escapeHtml(orbit.name)} — ${orbit.founded
          ? 'Kolonie mit C öffnen'
          : 'Landung im Reiter „Kolonie“ (C)'}</div>`
        : '')
      + (this.combat.count > 0
        ? `<div class="boostchip combat">Im Gefecht: ${escapeHtml(this.combat.name)}`
          + `${this.combat.count > 1 ? ` +${this.combat.count - 1}` : ''} — Abbau pausiert</div>`
        : '')
      + (fleet
        ? `<div class="boostchip fleet">Feld-Buff: ${escapeHtml(fleetName)} ${timeStr(fleet.buffLeft)}</div>`
        : '')
      + (anomaly ? `<div class="boostchip anomaly">${escapeHtml(anomaly)}</div>` : '')
      + active
        .map((entry) => `<div class="boostchip">${escapeHtml(entry.name)} ${timeStr(store.boosterLeft(entry.id))}</div>`)
        .join('');
  }

  private setNetStatus(status: { online: boolean; players: number; field: string }): void {
    this.nodes.netStatus.textContent = status.online
      ? `● ${escapeHtml(status.field)} · ${status.players} im Feld`
      : '○ Solo';
    this.nodes.netStatus.classList.toggle('online', status.online);
  }

  toast(message: string, tone = ''): void {
    const item = document.createElement('div');
    item.className = 'toast ' + tone;
    item.textContent = message;
    this.nodes.toasts.appendChild(item);
    setTimeout(() => {
      item.style.transition = 'opacity .4s';
      item.style.opacity = '0';
      setTimeout(() => item.remove(), 400);
    }, 2600);
    while (this.nodes.toasts.children.length > 4) this.nodes.toasts.firstChild?.remove();
  }
}

export { resource };
